import { useCallback, useEffect, useState } from 'react';

import { useApi } from '@/hooks/api';
import { getNoteKey, getNotesFromStorage, saveNotesToStorage } from '@/utils/emotion-detector/journalStorage';

import { JournalEntriesResponse, JournalEntry } from './types';

/**
 * Fetches journal entries and manages local notes
 */
export function useJournalEntries() {
	const [entries, setEntries] = useState<JournalEntry[]>([]);
	const [notes, setNotes] = useState<Record<string, string>>({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const apiRequest = useApi();

	const fetchEntries = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const response = await apiRequest<JournalEntriesResponse>({
				endpoint: 'v2/nvision/journal',
				method: 'GET',
				noCache: true,
			});

			setEntries(response.data.entries || []);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch journal entries');
		} finally {
			setLoading(false);
		}
	}, [apiRequest]);

	useEffect(() => {
		fetchEntries().catch(() => {});
		setNotes(getNotesFromStorage());
	}, [fetchEntries]);

	const handleNoteChange = useCallback(
		(key: string, value: string) => {
			const updated = { ...notes, [key]: value };
			setNotes(updated);
			saveNotesToStorage(updated);
		},
		[notes],
	);

	const deleteNote = useCallback(
		(key: string) => {
			const updated = { ...notes };
			delete updated[key];
			setNotes(updated);
			saveNotesToStorage(updated);
		},
		[notes],
	);

	const deleteEntry = useCallback(
		async (entryId: string) => {
			setDeletingId(entryId);

			try {
				await apiRequest({
					endpoint: `v2/nvision/journal/${entryId}`,
					method: 'DELETE',
				});

				setEntries(prevEntries => prevEntries.filter(entry => entry.id !== entryId));

				const noteKey = getNoteKey(entryId);
				if (notes[noteKey]) {
					deleteNote(noteKey);
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to delete journal entry');
				throw err;
			} finally {
				setDeletingId(null);
			}
		},
		[apiRequest, notes, deleteNote],
	);

	return {
		entries,
		setEntries,
		notes,
		handleNoteChange,
		deleteNote,
		deleteEntry,
		deletingId,
		loading,
		error,
		setError,
	};
}

export type { JournalEntry };
